import { SessionEndedError, tokenSubject } from "../stores/sessionGuard";

/**
 * Tearing a Feathers socket connection down when a tab refuses a foreign
 * credential, and bringing it back. Kept apart from apiClient.ts (which owns
 * building the socket) so the RootStore can use it without importing the
 * client's construction; the state is keyed by the socket, shared by both.
 */

/**
 * Per-socket bookkeeping for tearing the connection down and bringing it back
 * (see {@link suspendSocket}). Keyed by the socket so the hosts that have only
 * the Feathers app can reach it, and a double without an entry still works.
 */
export interface SocketLink {
  suspended: boolean;
  /** Bumped by every suspend: work begun before one must not act after it. */
  generation: number;
  /** Forget the credentials the next handshake would present (the client's `firstLoadAuth`). */
  forgetAuth?: () => void;
}
const socketLinks = new WeakMap<object, SocketLink>();
export const linkFor = (socket: object): SocketLink => {
  let link = socketLinks.get(socket);
  if (!link) {
    link = { suspended: false, generation: 0 };
    socketLinks.set(socket, link);
  }
  return link;
};

type BufferedPacket = { id?: number; data?: unknown[] };
type AckCallback = ((...args: unknown[]) => unknown) & { withError?: boolean };
type SocketLike = {
  disconnect?: () => unknown;
  connect?: () => unknown;
  auth?: unknown;
  io?: { opts?: { query?: unknown } };
  /** socket.io-client's offline send buffer: kept by disconnect(), flushed by connect(). */
  sendBuffer?: BufferedPacket[];
  /** Pending ack callbacks by packet id. */
  acks?: Record<string, AckCallback | undefined>;
};
type ClientLike = {
  io?: SocketLike;
  authentication?: { reset?: () => unknown };
};

/**
 * Empty socket.io-client's offline send buffer, and answer the calls waiting in
 * it so their callers do not hang until an ack timeout.
 *
 * `emit` on a socket that is not connected parks the packet in `sendBuffer`;
 * `disconnect()` leaves it there and `connect()` flushes it. A buffered call
 * can be the previous person's: the Feathers client's `disconnect` handler
 * queues a jwt re-authentication with their token, and a call started while the
 * network was down is queued under their identity. Delivered after the next
 * connect it signs the anonymous connection in as them, or runs their call as
 * them. So both ends of a suspension empty it.
 *
 * Each buffered call with an ack is rejected with {@link SessionEndedError}
 * (the code every store already treats as "the session this call belonged to
 * has ended"). The callback is invoked the way the Feathers client's own
 * request callback takes it, `(error, data)`; socket.io's `timeout()` wrapper
 * (`withError`) takes the error first too. Returns how many calls were dropped.
 */
export function discardBufferedCalls(socket: object | null | undefined): number {
  const target = socket as SocketLike | null | undefined;
  const buffer = target?.sendBuffer;
  if (!target || !Array.isArray(buffer) || buffer.length === 0) return 0;
  // Detach first: an ack callback that emits again must not be caught in this pass.
  const dropped = buffer.splice(0, buffer.length);
  for (const packet of dropped) rejectPacket(target, packet);
  return dropped.length;
}

/** Answer a packet taken out of the buffer: its ack (if it has one) fails with {@link SessionEndedError}. */
function rejectPacket(target: SocketLike, packet: BufferedPacket): void {
  const id = packet?.id;
  if (id === undefined || id === null) return;
  const ack = target.acks?.[String(id)];
  if (!ack) return;
  delete target.acks![String(id)];
  const path = typeof packet.data?.[1] === "string" ? packet.data[1] : "the request";
  try {
    ack.call(target, new SessionEndedError(path));
  } catch {
    // A caller that throws on its own rejection must not stop the rest.
  }
}

/** Is this buffered packet the Feathers client's jwt `authentication.create`? */
const isJwtAuthentication = (packet: BufferedPacket): packet is BufferedPacket & { data: unknown[] } => {
  const data = packet?.data;
  if (!Array.isArray(data) || data[0] !== "create" || data[1] !== "authentication") return false;
  const body = data[2] as { strategy?: unknown } | null | undefined;
  return typeof body === "object" && body !== null && body.strategy === "jwt";
};

/** Take the jwt re-authentications whose token's subject `matches` out of the buffer, failing each as session-ended. */
function dropReauthentications(
  target: SocketLike,
  buffer: BufferedPacket[],
  matches: (carriedSubject: string | undefined) => boolean,
): number {
  let dropped = 0;
  for (const packet of buffer.slice()) {
    if (!isJwtAuthentication(packet)) continue;
    const body = packet.data[2] as { accessToken?: unknown };
    if (!matches(tokenSubject(body.accessToken))) continue;
    buffer.splice(buffer.indexOf(packet), 1);
    rejectPacket(target, packet);
    dropped += 1;
  }
  return dropped;
}

/**
 * Settle the offline send buffer across a change of credentials, for a
 * connection that is about to be (re)opened with a new handshake.
 *
 * socket.io-client keeps what was emitted while the connection was down and
 * flushes it, in order, the moment the next one opens. Two things sit in it
 * after a network drop: the calls the app made meanwhile, and the Feathers
 * authentication client's own re-authentication, which its `disconnect` handler
 * queues with the access token in storage THEN. Both belong to the person the
 * previous connection was signed in as, and neither knows the credentials have
 * moved on since:
 *
 *  - the stored person CHANGED (another tab signed in as someone else, or out):
 *    nothing in the buffer is the new connection's, so all of it is dropped,
 *    failed with {@link SessionEndedError} (a code every store reads as "the
 *    session this call belonged to has ended", and the Feathers client does not
 *    read as a dead credential);
 *  - the SAME person, whose token was refreshed meanwhile: their calls are theirs
 *    and stay, but a queued jwt re-authentication would present the old access
 *    token after the handshake already presented the new one. An expired one is
 *    refused by the server, and the Feathers client answers that by clearing the
 *    fresh session; a still-valid one is accepted and written back over the
 *    fresh token in storage. So its token is replaced with the one now stored
 *    (and a re-authentication that carries a different person's token is dropped).
 *
 * `subjectBefore` is whom the connection is signed in as (from the Feathers
 * client's own `login`/`logout`), `subjectNow` whom the stored credentials the
 * next handshake presents belong to, both from the token `sub`
 * ({@link tokenSubject}). The buffer is dropped ONLY on evidence of a change of
 * person: a known person against a different known person, or against nobody
 * when `signedOut` says the credentials are gone. No known person before is no
 * evidence about the calls - a page loaded signed out that then signed in over
 * the open socket, or a sign-out (every path empties the buffer when it
 * suspends the connection) - and they are left alone, as they are when the
 * credentials merely cannot be read (opaque tokens). The one exception is a
 * sign-out: whoever the owner was, a queued re-authentication that carries a
 * person's token never goes out on the connection that now presents nobody.
 * Returns how many packets were dropped and how many re-authentications
 * rewritten.
 */
export function settleBufferedCalls(
  socket: object | null | undefined,
  credentials: {
    subjectBefore: string | undefined;
    subjectNow: string | undefined;
    /** The credentials about to be presented are gone, not merely unreadable. */
    signedOut?: boolean;
    accessToken?: string | null;
  },
): { dropped: number; rewritten: number } {
  const target = socket as SocketLike | null | undefined;
  const buffer = target?.sendBuffer;
  if (!target || !Array.isArray(buffer) || buffer.length === 0) return { dropped: 0, rewritten: 0 };
  const { subjectBefore, subjectNow, accessToken, signedOut } = credentials;
  if (subjectNow === undefined) {
    if (!signedOut) return { dropped: 0, rewritten: 0 };
    // Signed out. A known person's calls are all theirs and all go; without
    // one (the owner was never learned) the calls are no evidence, but a
    // re-authentication that carries a person's token is: it would sign the
    // anonymous connection in as them after they have gone.
    if (subjectBefore !== undefined) return { dropped: discardBufferedCalls(target), rewritten: 0 };
    return { dropped: dropReauthentications(target, buffer, (carried) => carried !== undefined), rewritten: 0 };
  }
  if (subjectBefore === undefined) return { dropped: 0, rewritten: 0 };
  if (subjectBefore !== subjectNow) return { dropped: discardBufferedCalls(target), rewritten: 0 };

  let dropped = 0;
  let rewritten = 0;
  for (const packet of buffer.slice()) {
    if (!isJwtAuthentication(packet)) continue;
    const body = packet.data[2] as { accessToken?: unknown };
    const carried = tokenSubject(body.accessToken);
    if (carried !== undefined && carried !== subjectNow) {
      buffer.splice(buffer.indexOf(packet), 1);
      rejectPacket(target, packet);
      dropped += 1;
    } else if (typeof accessToken === "string" && accessToken && body.accessToken !== accessToken) {
      packet.data[2] = { ...body, accessToken };
      rewritten += 1;
    }
  }
  return { dropped, rewritten };
}

/**
 * End the authenticated connection a person's session was using, WITHOUT
 * touching the credentials in storage every tab shares (so, unlike a logout,
 * the person who has signed in elsewhere keeps theirs).
 *
 * A tab that refuses a foreign credential clears its AuthStore and stores, but
 * its socket was still authenticated as the previous person, with every
 * ServiceStore listener on it live: a server event or a late call after the
 * purge would refill the stores for whoever signs in next. So this
 *
 *  1. resets the Feathers client's in-memory authentication, so its own
 *     `disconnect` handler does not re-authenticate from storage (which now
 *     holds somebody else's token);
 *  2. forgets what the handshake would present (`firstLoadAuth`, the
 *     handshake query, `socket.auth`), so nothing can reconnect under the old
 *     credentials; and
 *  3. empties the socket's offline send buffer ({@link discardBufferedCalls}):
 *     `disconnect()` keeps it, and a buffered re-authentication with the old
 *     token would sign the next connection in as the person who left; and
 *  4. disconnects. A manual disconnect is not retried by socket.io, and
 *     {@link resumeSocket} is the only thing that connects it again.
 *
 * Returns whether there was a socket to suspend.
 */
export function suspendSocket(app: unknown): boolean {
  const client = app as ClientLike | null | undefined;
  const socket = client?.io;
  if (!socket || typeof socket.disconnect !== "function") return false;
  const link = linkFor(socket);
  if (link.suspended) return true;
  link.suspended = true;
  link.generation += 1;
  try {
    // Synchronous in effect (it sets `authenticated = false`); must come before
    // the disconnect, whose handler re-authenticates while `authenticated`.
    void client?.authentication?.reset?.();
  } catch {
    // A client without it has nothing to re-authenticate.
  }
  link.forgetAuth?.();
  socket.auth = undefined;
  if (socket.io?.opts) delete socket.io.opts.query;
  socket.disconnect();
  // After the disconnect, so nothing it fires can put a call back in the buffer unseen.
  discardBufferedCalls(socket);
  return true;
}

/**
 * Bring a connection {@link suspendSocket} tore down back, with NO credentials
 * in its handshake: whatever signs in afterwards does it over the open socket
 * with its own authenticate, exactly as a login page's first load does. Called
 * when this tab starts a call that is the next session's (a sign-in, a token
 * refresh, anything begun after the purge) or takes up the origin's session
 * from storage. A no-op unless suspended.
 */
export function resumeSocket(app: unknown): boolean {
  const socket = (app as ClientLike | null | undefined)?.io;
  if (!socket) return false;
  const link = socketLinks.get(socket);
  if (!link?.suspended) return false;
  link.suspended = false;
  // Calls made while it was down belong to whoever was signed in then.
  discardBufferedCalls(socket);
  socket.connect?.();
  return true;
}

/** Has {@link suspendSocket} torn this app's connection down, and nothing brought it back? */
export function isSocketSuspended(app: unknown): boolean {
  const socket = (app as ClientLike | null | undefined)?.io;
  return !!socket && socketLinks.get(socket)?.suspended === true;
}
